import React, { useEffect, useRef, useState } from 'react';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, Popup, Tooltip, useMap } from 'react-leaflet';
import MapTouchGate from '../map/MapTouchGate';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import type { Block, CultivarArea, GeoJSONPolygon, GeneratedRow } from '../../types/orchard';
import CrosshairPolygonDraw from '../map/CrosshairPolygonDraw';
import { usePointerCoarse } from '../map/usePointerCoarse';
import { geoJSONToLeaflet as sharedGeoJSONToLeaflet, TILE_LAYERS, MAP_MAX_ZOOM } from '../../utils/leafletGeo';
import LayerToggleControl from '../map/LayerToggleControl';

// Fix for default marker icons in React-Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// =============================================================================
// TYPES
// =============================================================================

interface OrchardMapProps {
  farmBoundary?: GeoJSONPolygon;
  blocks?: Block[];
  cultivarAreas?: CultivarArea[];
  drawMode?: 'farm' | 'block' | 'cultivar-area' | 'first-row-line' | null;
  /** Existing polygon to edit in-place (vertices become draggable immediately). */
  editPolygon?: GeoJSONPolygon;
  onPolygonCreated?: (polygon: GeoJSONPolygon) => void;
  onPolygonEdited?: (polygon: GeoJSONPolygon) => void;
  /** First-row line support (planting plan). */
  firstRowLine?: { start: [number, number]; end: [number, number] } | null;
  onFirstRowLineCreated?: (line: { start: [number, number]; end: [number, number] }) => void;
  /** Generated row geometries to preview on the map (planting plan). */
  generatedRows?: GeneratedRow[];
  selectedEntityId?: number;
  selectedEntityType?: 'farm' | 'block' | 'cultivar-area';
  center?: [number, number];
  zoom?: number;
  /** [south, north, west, east] — when set, map animates to this bounding box. */
  focusBounds?: [number, number, number, number] | null;
}

// =============================================================================
// DRAW CONTROL COMPONENT
// =============================================================================

function DrawControl({
  drawMode,
  editPolygon,
  onPolygonCreated,
  onPolygonEdited,
}: {
  drawMode: string | null;
  editPolygon?: GeoJSONPolygon;
  onPolygonCreated?: (polygon: GeoJSONPolygon) => void;
  onPolygonEdited?: (polygon: GeoJSONPolygon) => void;
}) {
  const map = useMap();
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const pointerCoarse = usePointerCoarse();
  const [polygonCreated, setPolygonCreated] = useState(false);

  // -----------------------------------------------------------------------
  // Touch path: crosshair-based drawing. Skips leaflet-draw's polygon
  // handler entirely and mounts only the edit toolbar so that once a
  // polygon is placed it can still be fine-tuned via draggable vertices.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!drawMode || !pointerCoarse) return;
    const drawnItems = drawnItemsRef.current;
    map.addLayer(drawnItems);

    const editControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: false,
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: drawnItems,
        remove: true,
      },
    });
    editControl.addTo(map);

    const handleEdited = (event: any) => {
      event.layers.eachLayer((layer: any) => {
        const geoJSON = layer.toGeoJSON();
        const polygon: GeoJSONPolygon = {
          type: 'Polygon',
          coordinates: geoJSON.geometry.coordinates,
        };
        if (onPolygonEdited) onPolygonEdited(polygon);
      });
    };
    const handleDeleted = () => {
      drawnItems.clearLayers();
      setPolygonCreated(false);
    };
    map.on(L.Draw.Event.EDITED, handleEdited);
    map.on(L.Draw.Event.DELETED, handleDeleted);

    return () => {
      editControl.remove();
      map.off(L.Draw.Event.EDITED, handleEdited);
      map.off(L.Draw.Event.DELETED, handleDeleted);
      map.removeLayer(drawnItems);
      drawnItems.clearLayers();
      setPolygonCreated(false);
    };
  }, [map, drawMode, pointerCoarse, onPolygonEdited]);

  const handleCrosshairComplete = (polygon: GeoJSONPolygon) => {
    // Add the finished polygon to the edit feature group so the user can
    // keep tweaking it via the standard edit toolbar.
    const drawnItems = drawnItemsRef.current;
    const latlngs: [number, number][] = polygon.coordinates[0]
      .slice(0, -1)
      .map((c) => [c[1], c[0]]);
    const layer = L.polygon(latlngs, {
      color: '#3b82f6',
      weight: 2,
      fillOpacity: 0.2,
    });
    drawnItems.addLayer(layer);
    setPolygonCreated(true);
    if (onPolygonCreated) onPolygonCreated(polygon);
  };

  // -----------------------------------------------------------------------
  // Desktop path: edit-in-place (existing polygon provided)
  // Load the polygon into drawnItems and auto-activate vertex editing.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!drawMode || pointerCoarse || !editPolygon) return;

    const drawnItems = drawnItemsRef.current;
    map.addLayer(drawnItems);

    const latlngs = editPolygon.coordinates[0]
      .slice(0, -1)
      .map((c) => [c[1], c[0]] as [number, number]);
    const layer = L.polygon(latlngs, { color: '#3b82f6', weight: 2, fillOpacity: 0.2 });
    drawnItems.addLayer(layer);

    const editControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: false,
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: { featureGroup: drawnItems, remove: false },
    });
    editControl.addTo(map);

    // Auto-activate vertex editing so the user can drag immediately
    const enableTimer = setTimeout(() => {
      try {
        (editControl as any)._toolbars.edit.enable();
      } catch { /* ignore – toolbar may not be ready */ }
    }, 150);

    const handleEdited = (event: any) => {
      event.layers.eachLayer((l: any) => {
        const geoJSON = l.toGeoJSON();
        const polygon: GeoJSONPolygon = { type: 'Polygon', coordinates: geoJSON.geometry.coordinates };
        if (onPolygonEdited) onPolygonEdited(polygon);
      });
    };

    map.on(L.Draw.Event.EDITED, handleEdited);

    return () => {
      clearTimeout(enableTimer);
      editControl.remove();
      map.off(L.Draw.Event.EDITED, handleEdited);
      map.removeLayer(drawnItems);
      drawnItems.clearLayers();
    };
  }, [map, drawMode, pointerCoarse, editPolygon, onPolygonEdited]);

  // -----------------------------------------------------------------------
  // Desktop path: draw a new polygon from scratch (no existing polygon)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!drawMode || pointerCoarse || editPolygon) return;

    const drawnItems = drawnItemsRef.current;
    map.addLayer(drawnItems);

    // Track vertex count via closure so button state can be updated
    let vertexCount = 0;
    let finishBtn: HTMLElement | null = null;

    const updateFinishBtn = () => {
      if (finishBtn) {
        const enabled = vertexCount >= 3;
        finishBtn.style.opacity = enabled ? '1' : '0.5';
        (finishBtn.style as any).pointerEvents = enabled ? 'auto' : 'none';
      }
    };

    // Create the polygon handler directly — this gives us control over auto-start
    // and allows calling _finishShape() from our own button.
    // touchTolerance:0 prevents accidental polygon close on touch devices when
    // tapping near the first vertex.
    const handler: any = new (L as any).Draw.Polygon(map, {
      allowIntersection: true,
      showArea: true,
      touchTolerance: 0,
      shapeOptions: {
        color: '#3b82f6',
        weight: 2,
        fillOpacity: 0.2,
      },
    });

    // "Finish" button — becomes active once 3+ vertices are placed
    const FinishControl = L.Control.extend({
      onAdd() {
        const c = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const btn = L.DomUtil.create('a', '', c) as HTMLAnchorElement;
        btn.href = '#';
        btn.innerHTML = '&#10003; Finish';
        btn.title = 'Complete polygon (need 3+ points)';
        btn.style.cssText =
          'display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;padding:0 10px;height:34px;background:#16a34a;color:white;text-decoration:none;cursor:pointer;white-space:nowrap;opacity:0.5;pointer-events:none;';
        finishBtn = btn;
        L.DomEvent.on(btn, 'click', (e) => {
          L.DomEvent.preventDefault(e);
          if (vertexCount >= 3) handler._finishShape();
        });
        return c;
      },
      onRemove() {
        finishBtn = null;
      },
    });

    // "Undo" button — removes the last placed vertex
    const UndoControl = L.Control.extend({
      onAdd() {
        const c = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        const btn = L.DomUtil.create('a', '', c) as HTMLAnchorElement;
        btn.href = '#';
        btn.innerHTML = '&#8617; Undo';
        btn.title = 'Remove last point';
        btn.style.cssText =
          'display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;padding:0 10px;height:34px;background:#f59e0b;color:white;text-decoration:none;cursor:pointer;white-space:nowrap;';
        L.DomEvent.on(btn, 'click', (e) => {
          L.DomEvent.preventDefault(e);
          handler.deleteLastVertex();
          vertexCount = Math.max(0, vertexCount - 1);
          updateFinishBtn();
        });
        return c;
      },
    });

    // Edit-only control so the drawn polygon can be adjusted or deleted after drawing
    const editControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: false,
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: drawnItems,
        remove: true,
      },
    });

    const finishControl = new (FinishControl as any)({ position: 'topright' });
    const undoControl = new (UndoControl as any)({ position: 'topright' });

    finishControl.addTo(map);
    undoControl.addTo(map);
    editControl.addTo(map);

    const handleDrawVertex = () => {
      vertexCount++;
      updateFinishBtn();
    };

    const handleDrawStop = () => {
      vertexCount = 0;
    };

    const handleCreated = (event: any) => {
      const layer = event.layer;
      const geoJSON = layer.toGeoJSON();
      const polygon: GeoJSONPolygon = {
        type: 'Polygon',
        coordinates: geoJSON.geometry.coordinates,
      };
      if (onPolygonCreated) onPolygonCreated(polygon);
      drawnItems.addLayer(layer);
    };

    const handleEdited = (event: any) => {
      event.layers.eachLayer((layer: any) => {
        const geoJSON = layer.toGeoJSON();
        const polygon: GeoJSONPolygon = {
          type: 'Polygon',
          coordinates: geoJSON.geometry.coordinates,
        };
        if (onPolygonEdited) onPolygonEdited(polygon);
      });
    };

    const handleDeleted = () => {
      drawnItems.clearLayers();
    };

    map.on(L.Draw.Event.DRAWVERTEX, handleDrawVertex);
    map.on(L.Draw.Event.DRAWSTOP, handleDrawStop);
    map.on(L.Draw.Event.CREATED, handleCreated);
    map.on(L.Draw.Event.EDITED, handleEdited);
    map.on(L.Draw.Event.DELETED, handleDeleted);

    // Auto-start drawing so the user can immediately click points without
    // needing to find and click the toolbar polygon button first.
    // Cancelled on cleanup: enabling a handler on a map that has since
    // unmounted throws from inside Leaflet.
    const enableTimer = setTimeout(() => handler.enable(), 100);

    return () => {
      clearTimeout(enableTimer);
      handler.disable();
      finishControl.remove();
      undoControl.remove();
      editControl.remove();
      map.off(L.Draw.Event.DRAWVERTEX, handleDrawVertex);
      map.off(L.Draw.Event.DRAWSTOP, handleDrawStop);
      map.off(L.Draw.Event.CREATED, handleCreated);
      map.off(L.Draw.Event.EDITED, handleEdited);
      map.off(L.Draw.Event.DELETED, handleDeleted);
      map.removeLayer(drawnItems);
    };
  }, [map, drawMode, pointerCoarse, editPolygon, onPolygonCreated, onPolygonEdited]);

  if (pointerCoarse && drawMode && !polygonCreated) {
    const initialVerts = editPolygon
      ? (editPolygon.coordinates[0].slice(0, -1).map((c) => [c[1], c[0]]) as [number, number][])
      : undefined;
    return (
      <CrosshairPolygonDraw
        onComplete={handleCrosshairComplete}
        color="#3b82f6"
        initialVertices={initialVerts}
      />
    );
  }
  return null;
}

// =============================================================================
// FIRST-ROW-LINE DRAW CONTROL
// =============================================================================
//
// Lightweight two-click line drawing — start point on first click, end point on
// second click. Used for the planting plan to set row direction + starting
// row geometry on top of an existing cultivar-area polygon.

function FirstRowLineDraw({
  active,
  onComplete,
}: {
  active: boolean;
  onComplete?: (line: { start: [number, number]; end: [number, number] }) => void;
}) {
  const map = useMap();
  const [startPt, setStartPt] = useState<[number, number] | null>(null);
  const [cursorPt, setCursorPt] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!active) {
      setStartPt(null);
      setCursorPt(null);
      return;
    }

    const container = map.getContainer();
    const prevCursor = container.style.cursor;
    container.style.cursor = 'crosshair';

    const onClick = (e: L.LeafletMouseEvent) => {
      const pt: [number, number] = [e.latlng.lng, e.latlng.lat];
      if (!startPt) {
        setStartPt(pt);
      } else {
        onComplete?.({ start: startPt, end: pt });
        setStartPt(null);
        setCursorPt(null);
      }
    };
    const onMove = (e: L.LeafletMouseEvent) => {
      if (startPt) setCursorPt([e.latlng.lng, e.latlng.lat]);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setStartPt(null);
        setCursorPt(null);
      }
    };

    map.on('click', onClick);
    map.on('mousemove', onMove);
    document.addEventListener('keydown', onKey);

    return () => {
      map.off('click', onClick);
      map.off('mousemove', onMove);
      document.removeEventListener('keydown', onKey);
      container.style.cursor = prevCursor;
    };
  }, [active, map, startPt, onComplete]);

  if (!active || !startPt) return null;
  const positions: [number, number][] = [
    [startPt[1], startPt[0]],
    cursorPt ? [cursorPt[1], cursorPt[0]] : [startPt[1], startPt[0]],
  ];
  return (
    <>
      <Polyline positions={positions} pathOptions={{ color: '#f59e0b', weight: 3, dashArray: '6 6' }} />
      <CircleMarker
        center={[startPt[1], startPt[0]]}
        radius={6}
        pathOptions={{ color: '#f59e0b', fillColor: '#fff', fillOpacity: 1, weight: 2 }}
      />
    </>
  );
}

// =============================================================================
// MAP VIEW UPDATER — reacts to async center/zoom prop changes after mount
// =============================================================================

function MapViewUpdater({
  center,
  zoom,
  focusBounds,
}: {
  center?: [number, number];
  zoom?: number;
  focusBounds?: [number, number, number, number] | null;
}) {
  const map = useMap();
  const hasFlown = useRef(false);
  // Initial one-shot fly — preserves existing behaviour when a farm is loaded.
  useEffect(() => {
    if (center && !hasFlown.current) {
      hasFlown.current = true;
      map.setView(center, zoom ?? map.getZoom(), { animate: true });
    }
  }, [map, center, zoom]);
  // Reactive focus — triggered by address search selections.
  useEffect(() => {
    if (!focusBounds) return;
    const [south, north, west, east] = focusBounds;
    map.flyToBounds(
      [
        [south, west],
        [north, east],
      ],
      { maxZoom: 17, duration: 1 }
    );
  }, [map, focusBounds]);
  return null;
}

// =============================================================================
// ORCHARD MAP COMPONENT
// =============================================================================

const OrchardMap: React.FC<OrchardMapProps> = ({
  farmBoundary,
  blocks = [],
  cultivarAreas = [],
  drawMode = null,
  editPolygon,
  onPolygonCreated,
  onPolygonEdited,
  firstRowLine,
  onFirstRowLineCreated,
  generatedRows,
  selectedEntityId,
  selectedEntityType,
  center,
  zoom,
  focusBounds,
}) => {
  const timezone = useOrgTimezone();
  const [mapLayer, setMapLayer] = useState<'street' | 'satellite'>('satellite');

  // Calculate map center and zoom
  const getMapCenter = (): [number, number] => {
    if (center) return center;
    if (farmBoundary?.coordinates && farmBoundary.coordinates[0]?.length > 0) {
      const coords = farmBoundary.coordinates[0];
      let sumLat = 0;
      let sumLng = 0;
      for (let i = 0; i < coords.length - 1; i++) {
        sumLng += coords[i][0];
        sumLat += coords[i][1];
      }
      const count = coords.length - 1;
      return [sumLat / count, sumLng / count];
    }
    // Default fallback to Melbourne, Australia
    return [-37.8136, 144.9631];
  };

  const getMapZoom = (): number => {
    if (zoom) return zoom;
    if (farmBoundary || blocks.length > 0 || cultivarAreas.length > 0) {
      return 15;
    }
    return 13;
  };

  // Convert GeoJSON coordinates to Leaflet LatLng format
  const geoJSONToLeaflet = sharedGeoJSONToLeaflet;

  return (
    <div className="h-full w-full" style={{ minHeight: '500px' }}>
      <MapContainer
        center={getMapCenter()}
        zoom={getMapZoom()}
        maxZoom={MAP_MAX_ZOOM}
        style={{ height: '100%', width: '100%', minHeight: '500px' }}
        className="rounded-lg z-0"
      >
        <TileLayer
          attribution={TILE_LAYERS[mapLayer].attribution}
          url={TILE_LAYERS[mapLayer].url}
          maxNativeZoom={TILE_LAYERS[mapLayer].maxNativeZoom}
          maxZoom={TILE_LAYERS[mapLayer].maxZoom}
        />

        <MapViewUpdater center={center} zoom={zoom} focusBounds={focusBounds} />
        <LayerToggleControl
          mapLayer={mapLayer}
          onToggle={() => setMapLayer((l) => (l === 'street' ? 'satellite' : 'street'))}
        />

        {/* Farm boundary */}
        {farmBoundary && (
          <Polygon
            positions={geoJSONToLeaflet(farmBoundary.coordinates)}
            pathOptions={{
              color: '#059669',
              weight: 3,
              fillOpacity: selectedEntityType === 'farm' ? 0.15 : 0.05,
            }}
          >
            <Popup>
              <div className="font-semibold">Farm Boundary</div>
            </Popup>
          </Polygon>
        )}

        {/* Blocks */}
        {blocks.map((block) => (
          <Polygon
            key={`block-${block.id}`}
            positions={geoJSONToLeaflet(block.boundary_geometry.coordinates)}
            pathOptions={{
              color: '#3b82f6',
              weight: 2,
              fillOpacity:
                selectedEntityType === 'block' && selectedEntityId === block.id
                  ? 0.3
                  : 0.15,
            }}
          >
            <Popup>
              <div className="min-w-[150px]">
                <h3 className="font-bold text-sm">{block.name}</h3>
                {block.area_hectares && (
                  <p className="text-xs">Area: {Number(block.area_hectares).toFixed(2)} ha</p>
                )}
                {block.cultivar_area_count !== undefined && (
                  <p className="text-xs">
                    Cultivar Areas: {block.cultivar_area_count}
                  </p>
                )}
              </div>
            </Popup>
          </Polygon>
        ))}

        {/* Cultivar areas */}
        {cultivarAreas.map((area) => (
          <Polygon
            key={`area-${area.id}`}
            positions={geoJSONToLeaflet(area.boundary_geometry.coordinates)}
            pathOptions={{
              color: '#8b5cf6',
              weight: 2,
              fillOpacity:
                selectedEntityType === 'cultivar-area' && selectedEntityId === area.id
                  ? 0.4
                  : 0.2,
            }}
          >
            <Popup>
              <div className="min-w-[200px]">
                <h3 className="font-bold text-sm mb-1">{area.name}</h3>
                {area.cultivar_name && (
                  <p className="text-xs">
                    <strong>Cultivar:</strong> {area.cultivar_name}
                    {area.cultivar_trade_name && ` (${area.cultivar_trade_name})`}
                  </p>
                )}
                {area.area_hectares && (
                  <p className="text-xs">
                    <strong>Area:</strong> {Number(area.area_hectares).toFixed(2)} ha
                  </p>
                )}
                {area.tree_count && (
                  <p className="text-xs">
                    <strong>Trees:</strong> {area.tree_count.toLocaleString()}
                  </p>
                )}
                {area.planting_date && (
                  <p className="text-xs">
                    <strong>Planted:</strong>{' '}
                    {formatLocalDateInTz(area.planting_date, timezone)}
                  </p>
                )}
                {area.training_system && (
                  <p className="text-xs">
                    <strong>Training:</strong> {area.training_system}
                  </p>
                )}
              </div>
            </Popup>
          </Polygon>
        ))}

        {/* Polygon draw control (farm / block / cultivar-area) */}
        {drawMode && drawMode !== 'first-row-line' && (
          <DrawControl
            drawMode={drawMode}
            editPolygon={editPolygon}
            onPolygonCreated={onPolygonCreated}
            onPolygonEdited={onPolygonEdited}
          />
        )}

        {/* First-row line draw control (planting plan) */}
        <FirstRowLineDraw
          active={drawMode === 'first-row-line'}
          onComplete={onFirstRowLineCreated}
        />

        {/* Existing first-row line (planting plan) */}
        {firstRowLine && (
          <>
            <Polyline
              positions={[
                [firstRowLine.start[1], firstRowLine.start[0]],
                [firstRowLine.end[1], firstRowLine.end[0]],
              ]}
              pathOptions={{ color: '#f59e0b', weight: 4 }}
            >
              <Tooltip permanent direction="top" offset={[0, -4]}>
                Row 1
              </Tooltip>
            </Polyline>
            <CircleMarker
              center={[firstRowLine.start[1], firstRowLine.start[0]]}
              radius={6}
              pathOptions={{ color: '#f59e0b', fillColor: '#fff', fillOpacity: 1, weight: 2 }}
            />
          </>
        )}

        {/* Generated row preview (planting plan) */}
        {generatedRows?.map((row) => {
          if (row.length_meters <= 0) return null;
          const positions: [number, number][] = [
            [row.start_point[1], row.start_point[0]],
            [row.end_point[1], row.end_point[0]],
          ];
          return (
            <Polyline
              key={`gen-row-${row.row_number}`}
              positions={positions}
              pathOptions={{
                color: row.row_number === 1 ? '#f59e0b' : '#8b5cf6',
                weight: row.row_number === 1 ? 3 : 2,
                opacity: 0.85,
              }}
            >
              <Tooltip sticky>
                <div className="text-xs">
                  <div>
                    <strong>Row {row.row_number}</strong> — {row.tree_count} trees
                  </div>
                  <div>{row.length_meters.toFixed(1)} m</div>
                </div>
              </Tooltip>
            </Polyline>
          );
        })}
        <MapTouchGate disabled={!!drawMode} />
      </MapContainer>
    </div>
  );
};

export default OrchardMap;
